import { useEffect, useState } from 'react'
import { FiEdit2, FiInfo, FiMail, FiShare2, FiTrash2, FiX } from 'react-icons/fi'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { deleteNewsActivity } from '@domain/features/news/news-feature'
import { loadUserById } from '@domain/features/user/user-feature'
import type { UserProfile } from '@domain/models/user'
import NewsShareModal from '@presentation/components/news/NewsShareModal'
import { useActivity } from '@presentation/hooks/useActivity'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useNewsFavourite } from '@presentation/hooks/useNewsFavourite'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import FavouriteBookmarkButton from '@presentation/components/ui/FavouriteBookmarkButton'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import CommentsSection from '@presentation/components/comments/CommentsSection'
import {
  normalizeMarkdownForRendering,
  stripMarkdownToPlainText,
} from '@presentation/helpers/markdown'
import SeoHead from '@presentation/seo/SeoHead'
import StructuredData from '@presentation/seo/StructuredData'
import { buildBreadcrumbList } from '@presentation/seo/StructuredDataBuilders'
import { sanitizeForMeta, toAbsoluteUrl } from '@presentation/seo/seo-constants'

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400" fill="%23E5E7EB"%3E%3Crect width="800" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239CA3AF" font-size="24"%3ENo Image%3C/text%3E%3C/svg%3E'

interface AuthorPublicProfile {
  publicFullName?: string
  shortBio?: string
  publicContactEmail?: string
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function toAuthorPublicProfile(user: UserProfile): AuthorPublicProfile | null {
  const publicFullName = normalizeOptionalString(user.public_full_name)
  const shortBio = normalizeOptionalString(user.short_bio)
  const publicContactEmail = normalizeOptionalString(user.public_contact_email)

  if (!publicFullName && !shortBio && !publicContactEmail) {
    return null
  }

  return {
    publicFullName,
    shortBio,
    publicContactEmail,
  }
}

function hasAuthorPublicProfileData(profile: AuthorPublicProfile | null): boolean {
  return Boolean(
    profile?.publicFullName || profile?.shortBio || profile?.publicContactEmail,
  )
}

const articleMarkdownComponents: Components = {
  p: (props) => <p className="mt-4 first:mt-0" {...props} />,
  ul: (props) => <ul className="mt-4 list-disc space-y-1 pl-6" {...props} />,
  ol: (props) => <ol className="mt-4 list-decimal space-y-1 pl-6" {...props} />,
  li: (props) => <li className="text-text-primary" {...props} />,
  strong: (props) => <strong className="font-bold text-text-primary" {...props} />,
  a: ({ href, ...props }) => (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-primary underline underline-offset-2 transition-colors hover:text-primary-light"
    />
  ),
}

export default function NewsDetailPage() {
  const location = useLocation()
  useScrollToTopOnRouteEnter({ scrollOnPop: true })
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { activityId } = useParams<{ activityId: string }>()
  const { user } = useAuthUser()
  const { activity, loading, error } = useActivity(activityId)
  const [adminActionError, setAdminActionError] = useState<string | null>(null)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [bioDialogAuthorId, setBioDialogAuthorId] = useState<string | null>(null)
  const isAuthorBioOpen = bioDialogAuthorId !== null
  const [authorPublicProfilesById, setAuthorPublicProfilesById] = useState<
    Record<string, AuthorPublicProfile | null>
  >(
    {},
  )
  const {
    isFavourited,
    loading: favouriteLoading,
    toggling: favouriteToggling,
    error: favouriteError,
    toggle,
  } = useNewsFavourite(activity?.uid)

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const navigateBackToNews = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate('/news', { replace: true })
  }

  const handleEdit = () => {
    if (!activity?.uid) {
      return
    }
    navigate(`/news/${activity.uid}/edit`)
  }

  const handleDelete = async () => {
    if (!activity?.uid) {
      return
    }

    const confirmed = window.confirm('Are you sure you want to delete the article?')
    if (!confirmed) {
      return
    }

    setAdminActionError(null)
    try {
      await deleteNewsActivity(activity.uid, activity)
      navigate('/news')
    } catch (deleteError) {
      setAdminActionError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Failed to delete the article.',
      )
    }
  }

  const authorUserRefId = activity?.belongsToUser?.userRefId

  useEffect(() => {
    if (!authorUserRefId) {
      return
    }

    if (Object.prototype.hasOwnProperty.call(authorPublicProfilesById, authorUserRefId)) {
      return
    }

    let cancelled = false

    void loadUserById(authorUserRefId)
      .then((authorUser) => {
        if (cancelled) {
          return
        }
        const profile = toAuthorPublicProfile(authorUser)
        setAuthorPublicProfilesById((current) => {
          if (Object.prototype.hasOwnProperty.call(current, authorUserRefId)) {
            return current
          }
          return {
            ...current,
            [authorUserRefId]: profile,
          }
        })
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        setAuthorPublicProfilesById((current) => {
          if (Object.prototype.hasOwnProperty.call(current, authorUserRefId)) {
            return current
          }
          return {
            ...current,
            [authorUserRefId]: null,
          }
        })
      })

    return () => {
      cancelled = true
    }
  }, [authorPublicProfilesById, authorUserRefId])

  useEffect(() => {
    if (!isAuthorBioOpen) {
      return
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setBioDialogAuthorId(null)
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isAuthorBioOpen])

  if (loading) {
    return (
      <>
        <SeoHead
          title="Loading article | Parkinson's Insights"
          canonicalPath={activityId ? `/news/${activityId}` : '/news'}
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (!activity) {
    return (
      <>
        <SeoHead
          title="Article not found | Parkinson's Insights"
          canonicalPath={activityId ? `/news/${activityId}` : '/news'}
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {error ?? 'Article not found'}
          </p>
          <Link
            to="/news"
            className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
          >
            &larr; Back to News
          </Link>
        </div>
      </>
    )
  }

  const articleTitle = activity.title?.trim() || 'News article'
  const normalizedArticleMarkdown = normalizeMarkdownForRendering(
    activity.articleMarkdown ?? '',
  )
  const hasMarkdownArticle = normalizedArticleMarkdown.length > 0
  const articleBodyFallback = activity.article ?? ''
  const articleDescription = sanitizeForMeta(
    hasMarkdownArticle
      ? stripMarkdownToPlainText(normalizedArticleMarkdown) || articleTitle
      : articleBodyFallback || articleTitle,
    170,
  )
  const ogImageUrl =
    activity.image && !activity.image.startsWith('data:') ? activity.image : undefined
  const canonicalPath = `/news/${activity.uid}`
  const shareUrl = toAbsoluteUrl(canonicalPath)
  const shareImageUrl = ogImageUrl ? toAbsoluteUrl(ogImageUrl) : undefined
  const breadcrumbSchema = buildBreadcrumbList([
    { name: 'Home', pathOrUrl: '/' },
    { name: 'News', pathOrUrl: '/news' },
    { name: articleTitle, pathOrUrl: canonicalPath },
  ])
  const newsArticleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: articleTitle,
    description: articleDescription,
    url: toAbsoluteUrl(canonicalPath),
    mainEntityOfPage: toAbsoluteUrl(canonicalPath),
    datePublished: activity.publishTime?.toISOString(),
    dateModified: activity.publishTime?.toISOString(),
    author: activity.belongsToUser?.name
      ? {
          '@type': 'Person',
          name: activity.belongsToUser.name,
        }
      : {
          '@type': 'Organization',
          name: "Parkinson's Insights",
        },
    publisher: {
      '@type': 'Organization',
      name: "Parkinson's Insights",
      url: toAbsoluteUrl('/'),
    },
    image: ogImageUrl ? [toAbsoluteUrl(ogImageUrl)] : undefined,
  }
  const authorName = activity.belongsToUser?.name ?? ''
  const authorProfilePhoto = activity.belongsToUser?.profilePhoto
  const authorInitial = authorName.charAt(0).toUpperCase()
  const authorPublicProfile =
    authorUserRefId &&
    Object.prototype.hasOwnProperty.call(authorPublicProfilesById, authorUserRefId)
      ? authorPublicProfilesById[authorUserRefId] ?? null
      : null
  const authorProfileName = authorPublicProfile?.publicFullName
  const authorProfileBio = authorPublicProfile?.shortBio
  const authorProfileEmail = authorPublicProfile?.publicContactEmail
  const canOpenAuthorProfile = hasAuthorPublicProfileData(authorPublicProfile)
  const authorAvatarAlt = (authorProfileName ?? authorName) || 'Author'
  const isAuthorBioVisible =
    bioDialogAuthorId === authorUserRefId && canOpenAuthorProfile

  return (
    <article className="bg-background">
      <SeoHead
        title={`${articleTitle} | Parkinson's News`}
        description={articleDescription}
        canonicalPath={canonicalPath}
        ogType="article"
        imageUrl={ogImageUrl}
      />
      <StructuredData data={[breadcrumbSchema, newsArticleSchema]} />
      {/* Hero image */}
      <div className="relative h-64 w-full overflow-hidden bg-border-light sm:h-80 lg:h-112">
        <img
          src={activity.image || PLACEHOLDER_IMAGE}
          alt={activity.title ?? ''}
          className="absolute inset-0 block h-full w-full object-cover"
        />
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          {/* Back link */}
          <button
            type="button"
            onClick={navigateBackToNews}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to News
          </button>

          <div className="ml-auto flex w-full items-start justify-end gap-2 sm:w-auto">
            {user?.isAdmin && (
              <>
                <button
                  type="button"
                  onClick={handleEdit}
                  aria-label="Edit article"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
                >
                  <FiEdit2 className="h-5 w-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleDelete()
                  }}
                  aria-label="Delete article"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10"
                >
                  <FiTrash2 className="h-5 w-5" aria-hidden />
                </button>
              </>
            )}
            <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center">
              <FavouriteBookmarkButton
                isFavourited={isFavourited}
                onClick={() => {
                  if (activity.uid) {
                    AnalyticsFeature.trackNewsFavoriteToggle(
                      screenPath,
                      'news_detail',
                      activity.uid,
                      Boolean(user),
                    )
                  }
                  void toggle(openLoginModal)
                }}
                disabled={favouriteLoading || favouriteToggling}
                className="h-11 shrink-0 gap-2 px-4 !bg-background !text-blue hover:!bg-blue/10"
                label={isFavourited ? 'Favorited' : 'Add To Favourites'}
                ariaLabel={isFavourited ? 'Remove article from favourites' : 'Add article to favourites'}
              />
              <button
                type="button"
                onClick={() => {
                  setIsShareModalOpen(true)
                }}
                aria-label="Share article"
                className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-blue bg-background px-4 text-blue transition-colors hover:bg-blue/10"
              >
                <FiShare2 className="h-5 w-5" aria-hidden />
                <span className="text-sm font-semibold">Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="mt-6 text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">
          {activity.title}
        </h1>

        {/* Date */}
        {activity.publishTime && (
          <p className="mt-3 text-sm font-bold text-primary">
            {activity.publishTime.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        )}

        {favouriteError && (
          <p className="mt-2 text-sm text-text-secondary">
            {favouriteError}
          </p>
        )}
        {adminActionError && (
          <p className="mt-2 text-sm text-text-secondary">
            {adminActionError}
          </p>
        )}

        {/* Filter chips */}
        {activity.filters && activity.filters.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {activity.filters.map((filter) => (
              <span
                key={filter}
                className="rounded-full border border-primary px-3 py-1 text-xs font-semibold text-primary"
              >
                {filter}
              </span>
            ))}
          </div>
        )}

        {/* Author */}
        {activity.belongsToUser?.name && !activity.promoted && (
          <>
            {canOpenAuthorProfile ? (
              <button
                type="button"
                onClick={() => {
                  setBioDialogAuthorId(authorUserRefId ?? null)
                }}
                className="group mt-6 flex items-center gap-3 text-sm font-semibold text-text-primary transition-colors hover:text-blue"
              >
                {authorProfilePhoto ? (
                  <img
                    src={authorProfilePhoto}
                    alt={authorName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {authorInitial}
                  </div>
                )}
                <span className="inline-flex items-center gap-1">
                  {authorName}
                  <FiInfo
                    className="h-4 w-4 text-text-primary transition-colors group-hover:text-blue"
                    aria-hidden="true"
                  />
                </span>
              </button>
            ) : (
              <div className="mt-6 flex items-center gap-3">
                {authorProfilePhoto ? (
                  <img
                    src={authorProfilePhoto}
                    alt={authorName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {authorInitial}
                  </div>
                )}
                <span className="text-sm font-semibold text-text-primary">
                  {authorName}
                </span>
              </div>
            )}
          </>
        )}

        {/* Article body */}
        {hasMarkdownArticle ? (
          <div className="mt-8 text-text-primary leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={articleMarkdownComponents}
            >
              {normalizedArticleMarkdown}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="mt-8 whitespace-pre-wrap text-text-primary leading-relaxed">
            {articleBodyFallback}
          </div>
        )}

        {/* Source URL */}
        {activity.sourceUrl && (
          <div className="mt-8 text-center">
            <a
              href={activity.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                AnalyticsFeature.trackExternalLinkOpen(
                  screenPath,
                  'news_detail',
                  'news_source',
                  activity.uid,
                  Boolean(user),
                )
              }}
              className="inline-flex items-center justify-center rounded-lg bg-blue px-6 py-3 text-sm font-semibold text-white no-underline transition-colors hover:bg-blue/90"
            >
              Visit Website
            </a>
          </div>
        )}

        {/* Divider */}
        <hr className="mt-10 border-border-light" />

        <CommentsSection
          activityExploreId={activity.uid}
          onAuthRequired={openLoginModal}
        />
      </div>
      <NewsShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false)
        }}
        shareUrl={shareUrl}
        title={articleTitle}
        description={articleDescription}
        imageUrl={shareImageUrl}
      />
      {isAuthorBioVisible && canOpenAuthorProfile && (
        <div className="fixed inset-0 z-60 bg-black/55">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close biography dialog"
            onClick={() => {
              setBioDialogAuthorId(null)
            }}
          />
          <div className="relative z-10 flex h-full w-full items-center justify-center p-4 md:p-6">
            <section
              role="dialog"
              aria-modal="true"
              aria-label="Author biography"
              className="relative w-full max-w-xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-4xl bg-background p-6 shadow-2xl sm:max-h-[calc(100vh-3rem)] sm:p-8"
            >
              <button
                type="button"
                onClick={() => {
                  setBioDialogAuthorId(null)
                }}
                aria-label="Close biography dialog"
                className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-background"
              >
                <FiX className="h-6 w-6" />
              </button>
              <div className="flex flex-col items-center gap-4 text-center sm:gap-5">
                {authorProfilePhoto ? (
                  <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full ring-2 ring-blue">
                    <img
                      src={authorProfilePhoto}
                      alt={authorAvatarAlt}
                      className="h-full w-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-primary/10 text-3xl font-bold text-primary ring-2 ring-blue">
                    {authorInitial}
                  </div>
                )}
                <div>
                  {authorProfileName && (
                    <p className="text-xl font-bold text-blue sm:text-2xl">
                      {authorProfileName}
                    </p>
                  )}
                  {authorProfileBio && (
                    <p className="mt-3 text-sm text-text-secondary sm:text-base">
                      {authorProfileBio}
                    </p>
                  )}
                  {authorProfileEmail && (
                    <a
                      href={`mailto:${authorProfileEmail}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue transition-colors hover:text-blue/80"
                    >
                      <FiMail className="h-4 w-4" aria-hidden="true" />
                      {authorProfileEmail}
                    </a>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </article>
  )
}
